/**
 * Parse SPOC personnel CSV: Name, Designation, Role, Department, Reports To
 * @returns {{ ok: true, rows: object[], summary: object } | { ok: false, error: string }}
 */

const EXPECTED_HEADERS = ["name", "designation", "role", "department", "reports to"];

function norm(s) {
  return String(s ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

function normalizeHeaderCell(h) {
  return norm(h).toLowerCase();
}

/** Minimal CSV row split (handles "..." quoted fields). */
function splitCsvLine(line) {
  const out = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQ) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQ = false;
        }
      } else {
        cur += c;
      }
    } else if (c === '"') {
      inQ = true;
    } else if (c === ",") {
      out.push(cur);
      cur = "";
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out.map((x) => norm(x));
}

function parseCsv(text) {
  const raw = String(text || "").replace(/^\uFEFF/, "");
  const lines = raw.split(/\r?\n/).filter((ln) => norm(ln) !== "");
  if (lines.length < 2) return { error: "CSV must include a header row and at least one data row." };
  const headerCells = splitCsvLine(lines[0]).map(normalizeHeaderCell);
  const missing = EXPECTED_HEADERS.filter((h, i) => headerCells[i] !== h);
  if (missing.length || headerCells.length < 5) {
    return {
      error: `Header must be: Name, Designation, Role, Department, Reports To (found: ${lines[0]})`,
    };
  }
  const dataRows = [];
  for (let r = 1; r < lines.length; r++) {
    const cells = splitCsvLine(lines[r]);
    if (cells.length < 5) continue;
    if (!norm(cells[0]) && !norm(cells[1])) continue;
    dataRows.push({
      name: norm(cells[0]),
      designation: norm(cells[1]),
      roleRaw: norm(cells[2]),
      department: norm(cells[3]),
      reportsToRaw: norm(cells[4]),
    });
  }
  if (dataRows.length === 0) return { error: "No data rows found." };
  return { dataRows };
}

export function normalizeRoleToken(roleRaw) {
  const r = norm(roleRaw).toLowerCase();
  if (!r) return "UNKNOWN";
  if (r.includes("lead") && r.includes("advisor")) return "LEAD_ADVISOR";
  if (r.includes("spoc") || r.includes("company spoc")) return "COMPANY_SPOC";
  if (r.includes("delegate")) return "COMPANY_DELEGATE";
  if (r.includes("advisor")) return "ADVISOR";
  return "UNKNOWN";
}

export function displayPerson(name, designation) {
  if (!name) return "";
  if (!designation) return name;
  return `${name} (${designation})`;
}

export function parseOrganizationalSetupCsv(text) {
  const parsed = parseCsv(text);
  if (parsed.error) return { ok: false, error: parsed.error };
  const { dataRows } = parsed;

  const rows = dataRows.map((d, idx) => {
    const roleNormalized = normalizeRoleToken(d.roleRaw);
    const selfDisplay = displayPerson(d.name, d.designation);
    const hasReportsTo = Boolean(norm(d.reportsToRaw));
    return {
      ...d,
      roleNormalized,
      selfDisplay,
      index: idx,
      /** Kept for compatibility; UI shows `reportsToRaw` only. */
      isRoot: !hasReportsTo,
      parentIndex: null,
      reportsToDisplay: hasReportsTo ? d.reportsToRaw : "-",
    };
  });

  const deptSet = new Set(rows.map((r) => r.department).filter(Boolean));
  const reportingLines = rows.filter((r) => norm(r.reportsToRaw)).length;
  const summary = {
    totalPeople: rows.length,
    departments: deptSet.size,
    topLevel: rows.length - reportingLines,
    reportingLines,
  };

  return {
    ok: true,
    rows,
    summary,
  };
}
